import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  ModalProps,
  Row,
  Select
} from 'antd'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useClassifierServiceCreateCuatmClassifierElementEndpoint,
  useClassifierServiceGetCuatmClassifierElementEndpoint,
  useClassifierServiceGetCuatmClassifierElementListEndpointKey,
  useClassifierServiceUpdateCuatmClassifierElementEndpoint
} from 'src/api/queries'
import { ApiError, CuatmSource, LocalitiesStatus } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { CUATMInfiniteSelect } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId ? t('cuatm.edit_title') : t('cuatm.add_cuatm'),
    button: hasId ? t('common.edit') : t('common.add')
  }
}

export const CUATMClassifierModal = ({ id, onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()

  const { title, button } = getModalText(!!id, t)

  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { data, isLoading } =
    useClassifierServiceGetCuatmClassifierElementEndpoint(
      {
        elementId: String(id)
      },
      [id],
      { enabled: !!id }
    )

  const { mutate: create } =
    useClassifierServiceCreateCuatmClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('cuatm.create_success'))
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetCuatmClassifierElementListEndpointKey
          ]
        })
        onCancel()
      }
    })

  const { mutate: edit } =
    useClassifierServiceUpdateCuatmClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('cuatm.edit_success'))
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetCuatmClassifierElementListEndpointKey
          ]
        })
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      ...values,
      parentId:
        data?.parent?.translation?.[currentLang?.value] === values?.parentId
          ? data?.parent?.id
          : values?.parentId
    }

    return id
      ? edit({ elementId: String(id), requestBody: payload })
      : create({ requestBody: values })
  }

  useEffect(() => {
    if (id) {
      form.setFieldsValue({
        ...data,
        parentId: data?.parent?.translation?.[currentLang?.value]
      })
    }
  }, [id, data])

  const statusOptions = Object.values(LocalitiesStatus).map((status) => ({
    label: t(`localities.${status}`),
    value: status
  }))

  return (
    <Modal
      onCancel={onCancel}
      title={title}
      maskClosable={false}
      footer={null}
      width={800}
      loading={isLoading}
      open
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <Form.Item
                  name={['translation', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                >
                  <Input placeholder={t('common.enter_text')} />
                </Form.Item>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item
              name="parentId"
              label={t('cuatm.parent')}
              rules={requiredRules}
            >
              <CUATMInfiniteSelect
                disabled={data?.source === CuatmSource.SEEDER}
                placeholder={t('cuatm.parent')}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="code"
              label={t('common.code')}
              rules={requiredRules}
            >
              <Input placeholder={t('common.code')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="statisticCode"
              label={t('cuatm.statistic_code')}
              rules={requiredRules}
            >
              <Input placeholder={t('cuatm.statistic_code')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="abbreviation" label={t('cuatm.abbreviation')}>
              <Input placeholder={t('cuatm.abbreviation')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="status"
              label={t('common.type')}
              rules={requiredRules}
            >
              <Select placeholder={t('common.type')} options={statusOptions} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex gap={16} justify="center">
              <Button onClick={onCancel}>{t('common.cancel')}</Button>
              <Button type="primary" onClick={onSave}>
                {button}
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
